import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { Beneficiar } from '@/interfaces/beneficiar';
import beneficiariesServices from '@/api/beneficiaries';

interface FilterParams {
  sortBen: string | undefined;
  pageBen: number | undefined;
  sizeBen: number | undefined;
  searchInput: string | undefined;
}

interface MetaData {
  totalPages: number;
  totalElements: number;
}

interface BeneficiaryState {
  beneficiaries: { [key: string]: Beneficiar[] };
  selectedBen: Beneficiar;
  loading: boolean;
  error: string;
  currFilters: string;
  metaData: { [key: string]: MetaData };
  params: FilterParams;
}

const initialState: BeneficiaryState = {
  beneficiaries: {},
  selectedBen: {} as Beneficiar,
  loading: false,
  error: '',
  currFilters: '',
  metaData: {},
  params: {
    sortBen: undefined,
    pageBen: undefined,
    sizeBen: undefined,
    searchInput: undefined,
  },
};

export const fetchBeneficiaries = createAsyncThunk(
  'beneficiary/beneficiaries',
  async ({ size, page, sort, searchInput }: { size: number; page: number; sort: string; searchInput: string | undefined }, thunkAPI) => {
    try {
      const result = await beneficiariesServices.loadBeneficiaries({ size, page: page - 1, sort, searchInput });
      return result;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.data);
    }
  }
);

export const createNewBen = createAsyncThunk('beneficiary/createNew', async (data: any, thunkAPI) => {
  try {
    await beneficiariesServices.createBeneficiary(data);
    const { params } = (thunkAPI.getState() as RootState).beneficiary;
    const opts = {
      page: params.pageBen ?? 1,
      size: params.sizeBen ?? 5,
      sort: params.sortBen ?? 'name,desc',
      searchInput: params.searchInput,
    };
    await thunkAPI.dispatch(handleParamsChange(opts));
    await thunkAPI.dispatch(fetchBeneficiaries(opts));
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.data);
  }
});

export const deleteBen = createAsyncThunk('beneficiary/deleteBen', async (id: string, thunkAPI) => {
  try {
    const result = await beneficiariesServices.deleteBeneficiary(id);
    return result;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.data);
  }
});

export const updateBen = createAsyncThunk('beneficiary/updateBen', async ({ id, data }: { id: string; data: any }, thunkAPI) => {
  try {
    const result = await beneficiariesServices.updateBeneficiary(id, data);
    return result;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.data);
  }
});

export const beneficiarySlice = createSlice({
  name: 'beneficiary',
  initialState,
  reducers: {
    setSelectedBen: (state, action: PayloadAction<Beneficiar>) => {
      state.selectedBen = action.payload;
    },
    updateBeneficiary: (state, action: PayloadAction<Beneficiar>) => {
      Object.keys(state.beneficiaries).forEach(key => {
        state.beneficiaries[key] = state.beneficiaries[key].map(el => {
          if (el.id === action.payload.id) {
            return action.payload;
          }
          return el;
        });
      });
    },
    refresh: () => initialState,
    clearBeneficiaries: state => {
      state.beneficiaries = {};
    },
    handleParamsChange: (state, action: PayloadAction<{ size: number; page: number; sort: string; searchInput: string | undefined }>) => {
      const beneficiaryFilters = JSON.stringify(action.payload);
      state.currFilters = beneficiaryFilters;
      state.params.sortBen = action.payload.sort;
      state.params.pageBen = action.payload.page;
      state.params.sizeBen = action.payload.size;
      state.params.searchInput = action.payload.searchInput;
    },
  },
  extraReducers: builder => {
    builder.addCase(deleteBen.pending, state => {
      state.loading = true;
    });
    builder.addCase(deleteBen.fulfilled, state => {
      state.selectedBen = {} as Beneficiar;
      state.beneficiaries = {};
      state.loading = false;
    });
    builder.addCase(deleteBen.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });

    builder.addCase(updateBen.pending, state => {
      state.loading = true;
    });
    builder.addCase(updateBen.fulfilled, (state, action) => {
      Object.keys(state.beneficiaries).forEach(key => {
        state.beneficiaries[key] = state.beneficiaries[key].map(el => {
          if (el.id === state.selectedBen.id) {
            return action.payload;
          }
          return el;
        });
      });
      state.loading = false;
    });
    builder.addCase(updateBen.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });

    builder.addCase(fetchBeneficiaries.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchBeneficiaries.fulfilled, (state, action) => {
      const { payload: data } = action;
      state.beneficiaries[state.currFilters] = data.data;
      state.metaData[state.currFilters] = { totalPages: data.totalPages, totalElements: data.totalElements };
      state.loading = false;
    });
    builder.addCase(fetchBeneficiaries.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });

    builder.addCase(createNewBen.pending, state => {
      state.loading = true;
    });
    builder.addCase(createNewBen.fulfilled, state => {
      state.loading = false;
    });
    builder.addCase(createNewBen.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });
  },
});

export const { handleParamsChange, setSelectedBen, refresh, clearBeneficiaries, updateBeneficiary } = beneficiarySlice.actions;

export const selectBeneficiaryData = (state: RootState) => state.beneficiary;

export default beneficiarySlice.reducer;
